import React from 'react';
import { Search, Filter, Star } from 'lucide-react';
import type { ResourceFilters } from '../../types/resources';
import { ResourceType, DifficultyLevel } from '../../types/resources';

interface ResourceFiltersProps {
  filters: ResourceFilters;
  onFiltersChange: (filters: ResourceFilters) => void;
  categories: Array<{ id: string; name: string; slug: string }>;
  resultCount: number;
}

export const ResourceFilters: React.FC<ResourceFiltersProps> = ({
  filters,
  onFiltersChange,
  categories,
  resultCount
}) => {
  const handleFilterChange = (key: keyof ResourceFilters, value: any) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const clearFilters = () => {
    onFiltersChange({});
  };

  const hasActiveFilters = Object.values(filters).some(value => 
    value !== undefined && value !== '' && value !== false
  );

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
        {/* Search */}
        <div className="lg:col-span-2 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Rechercher une ressource..."
            value={filters.search || ''}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Category */}
        <select
          value={filters.category || ''}
          onChange={(e) => handleFilterChange('category', e.target.value || undefined)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">Toutes les thématiques</option>
          {categories.map((category) => (
            <option key={category.id} value={category.slug}>
              {category.name}
            </option>
          ))}
        </select>

        {/* Type */}
        <select
          value={filters.type || ''}
          onChange={(e) => handleFilterChange('type', e.target.value as ResourceType || undefined)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">Tous les types</option>
          <option value="ARTICLE">Articles</option>
          <option value="GUIDE">Guides</option>
          <option value="CHECKLIST">Checklists</option>
          <option value="TOOL">Outils</option>
          <option value="VIDEO">Vidéos</option>
          <option value="DOCUMENT">Documents</option>
          <option value="LINK">Liens</option>
        </select>

        {/* Difficulty */}
        <select
          value={filters.difficulty || ''}
          onChange={(e) => handleFilterChange('difficulty', e.target.value as DifficultyLevel || undefined)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">Tous les niveaux</option>
          <option value="BEGINNER">Débutant</option>
          <option value="INTERMEDIATE">Intermédiaire</option>
          <option value="ADVANCED">Avancé</option>
          <option value="EXPERT">Expert</option>
        </select>

        {/* Featured */}
        <div className="flex items-center space-x-3">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.featured || false}
              onChange={(e) => handleFilterChange('featured', e.target.checked || undefined)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <div className="flex items-center space-x-1">
              <Star className="w-4 h-4 text-yellow-500" />
              <span className="text-sm text-gray-700">À la une</span>
            </div>
          </label>
        </div>
      </div>

      {/* Active Filters & Results */}
      <div className="mt-4 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Filter className="w-4 h-4" />
            <span>{resultCount} ressource{resultCount !== 1 ? 's' : ''}</span>
          </div>
          
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              Effacer les filtres
            </button>
          )}
        </div>

        {/* Active filters display */}
        {hasActiveFilters && (
          <div className="flex items-center space-x-2">
            <span className="text-xs text-gray-500">Filtres actifs :</span>
            {filters.search && (
              <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                "{filters.search}"
              </span>
            )}
            {filters.category && (
              <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
                {categories.find(c => c.slug === filters.category)?.name}
              </span>
            )}
            {filters.type && (
              <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                {filters.type}
              </span>
            )}
            {filters.difficulty && (
              <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full">
                {filters.difficulty}
              </span>
            )}
            {filters.featured && (
              <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                À la une
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};